import { useEffect } from 'react';
import { useHotkeysContext } from 'react-hotkeys-hook';

import { useGlobalState } from '@/state/global.state';

import { useHelpOverlayStore } from './helpOverlayStore';
import { HOTKEY_SCOPES } from './scopes';

/**
 * Bridges app overlay-open state → the `global` hotkey scope so action
 * shortcuts go inert whenever an overlay is open.
 *
 * Commit B (ENG-43, end state): the legacy keyboard bus and its `!allModalOpen`
 * gate are gone, so this is now the SOLE suppression authority. The `global`
 * scope (section action shortcuts — vulns Alt+T, etc.) is disabled when a modal
 * OR a drawer is open. Drawer-open is read from `drawerState.drawerOrder` (the
 * parsed `drawerOrder` URL param array). This means a section shortcut will not
 * fire while a drawer is open — the approved D3 behavior.
 *
 * Only the `global` scope is toggled here, so `overlay`- and `tableNav`-scoped
 * bindings are never suppressed and in-drawer keyboard navigation keeps working:
 * Escape-to-dismiss, attack-path copy/paste, and POE Alt+Arrow navigation
 * (`overlay`), plus table row arrow-nav (`tableNav`, used by the shared
 * `<Vulnerabilities>` list rendered inside the asset drawer). Each self-gates
 * via `enabled`/element refs rather than via this global gate.
 *
 * Single-writer invariant: this component is the ONLY thing that ever
 * enables/disables the `global` scope. The `?` help overlay (KeyboardHelpOverlay)
 * is a local-state Radix Dialog that does NOT set `modal.allModal.open`, so it
 * publishes its open state into `useHelpOverlayStore` and we fold that flag into
 * `overlayOpen` here. Because suppression is computed from ALL three sources
 * (modal OR drawer OR help) in one place, closing the help overlay while a
 * drawer is still open leaves `overlayOpen` true (drawer) and `global` stays
 * correctly suppressed — `global` only re-enables once every source is closed.
 *
 * The notifications and exposures drawers live outside `drawerState.drawerOrder`
 * and set no `modal.allModal` flag, so they are folded into `overlayOpen` here
 * via their own `notificationsDrawerOpen` / `exposuresDrawerOpen` flags — without
 * this, a section shortcut would still fire behind those two drawers.
 *
 * Rendered once, inside both GlobalStateProvider and HotkeysProvider.
 */
export function HotkeyScopeSync(): null {
  const { modal, drawerState } = useGlobalState();
  const helpOpen = useHelpOverlayStore(s => s.open);
  // In production these slices are always present (GlobalStateProvider). The
  // optional chaining only prevents a store-shape mismatch (e.g. a test's
  // partial mock that omits drawerState.drawerOrder) from hard-crashing the
  // React tree; a missing slice degrades to "no overlay open".
  const overlayOpen =
    !!modal?.allModal?.open ||
    (drawerState?.drawerOrder?.length ?? 0) > 0 ||
    !!drawerState?.notificationsDrawerOpen ||
    !!drawerState?.exposuresDrawerOpen ||
    helpOpen;
  const { enableScope, disableScope } = useHotkeysContext();

  useEffect(() => {
    if (overlayOpen) {
      disableScope(HOTKEY_SCOPES.global);
    } else {
      enableScope(HOTKEY_SCOPES.global);
    }
  }, [overlayOpen, enableScope, disableScope]);

  return null;
}
